import { jsPDF } from 'jspdf';
import { logoURL, robotoBoldFontTFF, robotoRegularFontTFF } from './constants';
import { PDFData } from './pdf';
import {
  addBorderedTableCol,
  addBorderlessTableCol,
  addIdImovelBox,
  addMapLegendItem,
  addSignatureSections,
} from './utils';

export type MapLegendData = {
  label: string;
  size?: 'small' | 'normal';
  borderColor?: string;
  fillColor?: string;
}[];

export function addLogo(doc: jsPDF) {
  doc.addImage(logoURL, 'PNG', 0.85, 0.85, 1.78, 1.77);
}

export function addFonts(doc: jsPDF) {
  doc.addFileToVFS('roboto-bold.tff', robotoBoldFontTFF);
  doc.addFileToVFS('roboto-regular.tff', robotoRegularFontTFF);
  doc.addFont('roboto-bold.tff', 'Roboto', 'bold');
  doc.addFont('roboto-regular.tff', 'Roboto', 'regular');
}

export function addPageBorders(doc: jsPDF) {
  doc.setDrawColor('black');
  doc.setLineWidth(0.08);

  doc.rect(0.7, 0.7, 19.6, 28.3);
}

/*
 ****TOP****
 */
export function addMainTitle(doc: jsPDF) {
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(14);
  doc.text('PREFEITURA MUNICIPAL DE BALNEÁRIO RINCÃO', 4, 1.42);
  doc.text('CONSULTA PRÉVIA ELETRÔNICA', 6, 2.07);
}

export function addMainSubtitle(doc: jsPDF) {
  doc.setFont('Roboto', 'regular');
  doc.setFontSize(8);
  doc.text('ESTE DOCUMENTO NÃO AUTORIZA A CONSTRUIR', 6.6, 2.57);
}

export function addReduzidoBox(doc: jsPDF, num: string) {
  const reduzidoBoxWidth = 3.5;

  doc.setDrawColor('black');
  doc.setLineWidth(0.04);

  doc.rect(16.5, 0.9, reduzidoBoxWidth, 1.7);

  doc.setFont('Roboto', 'bold');
  doc.setFontSize(12);
  doc.text('Nº REDUZIDO', 16.95, 1.4);

  doc.line(16.5, 1.6, 16.5 + reduzidoBoxWidth, 1.6);

  const availableWidthForNumText = reduzidoBoxWidth;
  const numTextWidth = doc.getTextWidth(num);
  const diffAvailAndNumTextWidth = availableWidthForNumText - numTextWidth;

  doc.text(num, 16.5 + diffAvailAndNumTextWidth / 2, 2.2);
}

export function addIdImovelSection(
  doc: jsPDF,
  data: {
    insc: string;
    prop: string;
    loteamento: string;
    quadra: string;
    lote: string;
    areaTerr: string;
    testada: string;
    areaConstr: string;
    logra: string;
    num: string;
  }
) {
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(9);

  doc.text('IDENTIFICAÇÃO DO IMÓVEL', 0.95, 3);

  addIdImovelBox(doc, 0.95, 3.1, 3, 'INSCRIÇÃO IMOBILIÁRIA', data.insc);
  addIdImovelBox(doc, 4.05, 3.1, 15.97, 'PROPRIETÁRIO', data.prop);
  addIdImovelBox(doc, 0.95, 4.1, 11.5, 'LOTEAMENTO', data.loteamento);
  addIdImovelBox(doc, 12.55, 4.1, 1.5, 'QUADRA', data.quadra);
  addIdImovelBox(doc, 14.15, 4.1, 1.2, 'LOTE', data.lote);
  addIdImovelBox(doc, 0.95, 5.1, 13.1, 'LOGRADOURO', data.logra);
  addIdImovelBox(doc, 14.15, 5.1, 1.2, 'N°', data.num);
  addIdImovelBox(doc, 0.95, 6.1, 4.7, 'TESTADA', data.testada);
  addIdImovelBox(doc, 5.75, 6.1, 4.7, 'ÁREA CONSTRUÍDA', data.areaConstr);
  addIdImovelBox(doc, 10.55, 6.1, 4.8, 'ÁREA DO TERRENO', data.areaTerr);
}

export function addImovelImg(doc: jsPDF, imovelImg?: string) {
  doc.setDrawColor('#ababab');
  doc.setLineWidth(0.04);

  doc.rect(15.45, 4.1, 4.57, 2.9);
  if (imovelImg) {
    doc.addImage(imovelImg, 'JPEG', 15.49, 4.14, 4.49, 2.83);
  }
}

/*
 ****MAP****
 */
function addMapTitle(doc: jsPDF) {
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(9);

  doc.text('MAPA DE LOCALIZAÇÃO', 0.95, 7.4);
}

export function addMapImg(doc: jsPDF, mapImgURL: string) {
  doc.setDrawColor('#ababab');
  doc.setLineWidth(0.04);

  // 14.42 - 409
  // 7.92 - 225
  doc.rect(0.95, 7.5, 19.1, 8);
  doc.addImage(mapImgURL, 'undefined', 0.99, 7.54, 19.02, 7.92);
}

export function addMapaSection(doc: jsPDF, mapImgURL: string) {
  addMapTitle(doc);
  addMapImg(doc, mapImgURL);
}

function addLegendTitle(doc: jsPDF) {
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(9);

  doc.text('LEGENDA', 0.95, 15.9);
}

export function addLegendaSection(doc: jsPDF, legendData: MapLegendData) {
  addLegendTitle(doc);

  doc.setDrawColor('#ababab');
  doc.setLineWidth(0.04);

  doc.rect(0.95, 16, 19.1, 0.7);

  doc.setFont('Roboto', 'regular');
  doc.setFontSize(9);

  let xAcc = 1.2;

  legendData.forEach(({ label, borderColor, fillColor, size }, i) => {
    addMapLegendItem(doc, xAcc, 16.2, label, size, fillColor, borderColor);

    xAcc += 1.5 + doc.getTextWidth(label);
  });
}

export function addDistEsq(doc: jsPDF, dist: string) {
  doc.setFont('Roboto', 'regular');
  doc.setFontSize(8);

  doc.text('DISTÂNCIA À ESQUINA MAIS PRÓXIMA:', 11.5, 15.9);
  doc.text(
    dist,
    12 + doc.getTextWidth('DISTÂNCIA À ESQUINA MAIS PRÓXIMA:'),
    15.9
  );
}

/*
 ****TABLES****
 */
export function addBorderlessTable(
  doc: jsPDF,
  data: {
    ruas: string;
    sisViar: string;
    caixa: string;
    pista: string;
    passeio: string;
    cantCentral: string;
  }
) {
  addBorderlessTableCol(doc, 0.95, 17.2, 9.7, 'left', 'RUAS', data.ruas);

  addBorderlessTableCol(
    doc,
    10.9,
    17.2,
    3,
    'center',
    'SISTEMA VIÁRIO',
    data.sisViar
  );

  addBorderlessTableCol(doc, 14.2, 17.2, 1, 'center', 'CAIXA', data.caixa);

  addBorderlessTableCol(doc, 15.5, 17.2, 1, 'center', 'PISTA', data.pista);

  addBorderlessTableCol(
    doc,
    16.8,
    17.2,
    1.4,
    'center',
    'PASSEIO',
    data.passeio
  );

  addBorderlessTableCol(
    doc,
    18,
    17.2,
    2.5,
    'center',
    'CANTEIRO CENTRAL',
    data.cantCentral
  );
}

export function addBorderedTable(doc: jsPDF, data: PDFData['zona']) {
  addBorderedTableCol(doc, 0.7, 19, 3.9, 'Zona', undefined, data.tipo_comp);
  addBorderedTableCol(
    doc,
    4.6,
    19,
    1.2,
    'Índice apr.',
    undefined,
    data.indice_apr
  );
  addBorderedTableCol(
    doc,
    5.8,
    19,
    1.3,
    'Taxa de ocupação',
    undefined,
    data.taxa_ocupa
  );
  addBorderedTableCol(doc, 7.1, 19, 1.2, 'Núm. pav.', undefined, data.num_pav);
  addBorderedTableCol(doc, 8.3, 19, 2.6, 'Recuo frontal', [
    {
      title: 'Residencial',
      content: data.recuo_f_r,
      width: 1.4,
    },
    {
      title: 'Comercial',
      content: data.recuo_f_c,
      width: 1.2,
    },
  ]);
  addBorderedTableCol(
    doc,
    10.9,
    19,
    1.7,
    'Recuo lateral',
    undefined,
    data.recuo_lat
  );
  addBorderedTableCol(
    doc,
    12.6,
    19,
    1.7,
    'Recuo fundos',
    undefined,
    data.recuo_fun
  );
  addBorderedTableCol(
    doc,
    14.3,
    19,
    1.4,
    'Índice apr. da base',
    undefined,
    data.ind_apr_ba
  );
  addBorderedTableCol(
    doc,
    15.7,
    19,
    1.5,
    'Núm. pav. com outorga',
    undefined,
    data.num_pav_c
  );
  addBorderedTableCol(
    doc,
    17.2,
    19,
    1.5,
    'Índice apr. máximo',
    undefined,
    data.ind_apr_ma
  );
  addBorderedTableCol(
    doc,
    18.7,
    19,
    1.55,
    'Taxa de perm.',
    undefined,
    data.taxa_perme
  );
}

/*
 ****BOTTOM****
 */
function addObsImportantesTitle(doc: jsPDF) {
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(9);

  doc.text('OBSERVAÇÕES IMPORTANTES', 5, 21.85, { align: 'center' });
}

export function addObsImportantes(doc: jsPDF) {
  doc.setDrawColor('#ababab');
  doc.setLineWidth(0.04);
  doc.rect(0.7, 21.5, 8.8, 4.5);

  addObsImportantesTitle(doc);

  doc.line(0.7, 22, 9.5, 22);

  doc.setFont('Roboto', 'regular');
  doc.setFontSize(7);
  doc.text(
    `- Este documento não autoriza a construir e está sujeito a validação.
- Toda construção, reforma ou ampliação deve ter licença válida, emitida pela
Prefeitura Municipal.`,
    0.8,
    22.3
  );
}

function addDocsNecTitle(doc: jsPDF) {
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(9);

  doc.text('DOCUMENTOS NECESSÁRIOS PARA APROVAÇÃO DO PROJETO', 14.9, 21.85, {
    align: 'center',
  });
}

export function addDocsNec(doc: jsPDF, data: PDFData['docs']) {
  doc.setDrawColor('#ababab');
  doc.setLineWidth(0.04);
  doc.rect(9.5, 21.5, 10.8, 4.5);

  addDocsNecTitle(doc);
  doc.line(9.5, 22, 20.3, 22);

  doc.setFont('Roboto', 'regular');
  doc.setFontSize(7);
  doc.text(
    `( X ) Consulta Prévia.
( X ) Matrícula do Terreno Atualizada.
( X ) Projeto Arquitetônico.
( X ) Projeto Hidrossanitário.
( X ) Projeto Elétrico aprovado na Cooperativa para projetos acima de 120,00 m².
( X ) ART/RRT de Projeto e Execução.
( X ) Projeto de Cálculo Estrutural para obras acima de 2 pavimentos.
( X ) Projeto Preventivo de Combate ao Incêndio aprovado no Corpo de Bombeiros para
obras comerciais, industriais e/ou multifamiliares.
( X ) Habite-se de construções existentes sobre o terreno.
(${data.isWithinAPP ? ' X ' : ' '}) Autorização Ambiental.*
(${
      data.isWithinPreamar ? ' X ' : ' '
    }) Imóvel em área de Marinha - Autorização SPU - Secretaria do
Patrimônio da União.**`,
    9.6,
    22.3
  );
}

export function addEntsComp(doc: jsPDF) {
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(7);

  doc.text('ESPAÇO DESTINADO A ENTIDADES COMPETENTES', 0.8, 26.3);

  addSignatureSections(doc, 0.7, 26.4, 19.6, [
    {
      entity: 'PREFEITURA MUNICIPAL\nDE BALNEÁRIO RINCÃO',
    },
    {
      entity: 'SAMAE (SERVIÇO AUTÔNOMO\nMUNICIPAL DE ÁGUA E ESGOTO)',
    },
    {
      entity: 'COOPERALIANÇA\nCOOPERATIVA DE ELETRIFICAÇÃO',
    },
  ]);
}
